import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { authAPI } from '../services/api';

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  role: 'admin' | 'management' | 'bauleiter' | 'foreman' | 'installer';
}

interface AuthResponse {
  access_token: string;
  token_type: string;
  user_role: 'admin' | 'management' | 'bauleiter' | 'foreman' | 'installer';
  user_id: number;
}

interface ProfileData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  updateUserProfile: (data: ProfileData) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const isAuthenticated = user !== null;

  useEffect(() => {
    const checkSession = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const userData = await authAPI.getUser() as User;
          setUser(userData);
        }
      } catch (error) {
        console.error('Session check failed:', error);
        localStorage.removeItem('token');
      }
    };

    // Nur einmal beim Mount ausführen, nicht bei jeder Location-Änderung
    checkSession();
  }, []);

  // Separater useEffect für Login-Redirect
  useEffect(() => {
    if (user && location.pathname === '/login') {
      navigate('/dashboard');
    }
  }, [user, location.pathname, navigate]);

  const login = async (username: string, password: string) => {
    try {
      const response = await authAPI.login(username, password) as AuthResponse;
      localStorage.setItem('token', response.access_token);
      
      // Store refresh token for automatic token refresh
      if ((response as any).refresh_token) {
        localStorage.setItem('refresh_token', (response as any).refresh_token);
      }
      
      const userData = await authAPI.getUser() as User;
      setUser(userData);
      
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setUser(null);
    navigate('/login');
  };

  const updateUserProfile = async (data: ProfileData) => {
    try {
      // TODO: Implement real API call for profile update
      if (user) {
        const updatedUser = {
          ...user,
          ...data,
        };
        setUser(updatedUser);
      }
    } catch (error) {
      console.error('Profile update failed:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, login, logout, updateUserProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 